import { useEffect, useMemo, useState, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { isAnswered, missingRequired, type MissingItem } from "./answerValidation";
import { newOnlyQuestionIds } from "./newOnly";
import { FileUpload } from "./questions/FileUpload";
import { InfoBlock } from "./questions/InfoBlock";
import { LongText } from "./questions/LongText";
import { MultiChoice } from "./questions/MultiChoice";
import { NumberInput } from "./questions/NumberInput";
import { ShortText } from "./questions/ShortText";
import { SingleChoice } from "./questions/SingleChoice";
import { YesNo } from "./questions/YesNo";
import { ReviewPanel } from "./ReviewPanel";
import { REVIEW_ID, SectionNav } from "./SectionNav";
import type { AnswerValue, Answers, QuestionProps, ResponseOut, ResponsePatch, Section, SectionProgress, Template } from "./types";
import { useAutosave } from "./useAutosave";
import { ListChecks } from "lucide-react";
import { SavingIndicator } from "@/components/SavingIndicator";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Markdown } from "@/lib/markdown";

const NONE: string[] = [];

const COMPONENTS: Record<string, ComponentType<QuestionProps>> = {
  yes_no: YesNo,
  single_choice: SingleChoice,
  multi_choice: MultiChoice,
  short_text: ShortText,
  long_text: LongText,
  number: NumberInput,
  file_upload: FileUpload,
};

export type QuestionnaireFormProps = {
  template: Template;
  response: ResponseOut;
  onPatch: (patch: ResponsePatch) => Promise<void>;
  onMarkDone: (sectionId: string) => void;
  onSubmit: () => void;
  readOnly: boolean;
  submitting?: boolean;
  /** Section to open first (deep link from Home or the overview). */
  initialSectionId?: string;
  /** The answers were sent: the progress header says so instead of counting sections. */
  complete?: boolean;
  /** Version number; from version 2 a returning respondent sees only what is new or changed. */
  version?: number;
  /** Questions added since the previous version. */
  addedQuestionIds?: string[];
  /** Questions whose wording or options changed since the previous version. */
  changedQuestionIds?: string[];
  /** Staff read-only view: show what a returning client sees. */
  staffPreview?: boolean;
  /** The signed-in user, so "Answered by" only names colleagues. */
  currentUserId?: string;
};

export function QuestionnaireForm({
  template,
  response,
  onPatch,
  onMarkDone,
  onSubmit,
  readOnly,
  submitting = false,
  initialSectionId,
  complete = false,
  version = 1,
  addedQuestionIds = NONE,
  changedQuestionIds = NONE,
  staffPreview = false,
  currentUserId,
}: QuestionnaireFormProps) {
  const { t } = useTranslation();
  // Worked out once from the answers the form opened with, so a question being answered stays put.
  const [newOnlyIds] = useState(() =>
    newOnlyQuestionIds({
      template,
      version,
      addedQuestionIds,
      changedQuestionIds,
      reviewFlags: response.review_flags,
      initialAnswers: response.answers,
      staffPreview,
    }),
  );
  const isShown = (sectionId: string) => sectionId === REVIEW_ID || template.sections.some((x) => x.id === sectionId);
  const hasNew = (section: Section) => section.questions.some((q) => newOnlyIds?.has(q.id));
  // A deep link to a section with nothing new opens the full list.
  const [showAll, setShowAll] = useState(
    () =>
      newOnlyIds === null ||
      (initialSectionId !== undefined &&
        initialSectionId !== REVIEW_ID &&
        template.sections.some((x) => x.id === initialSectionId && !hasNew(x))),
  );
  const newOnly = newOnlyIds !== null && !showAll;
  const sections = newOnly ? template.sections.filter(hasNew) : template.sections;
  const [answers, setAnswers] = useState<Answers>(() => ({ ...response.answers }));
  const [progress, setProgress] = useState<Record<string, SectionProgress>>(() => ({ ...response.section_progress }));
  const [activeId, setActiveId] = useState<string>(() =>
    initialSectionId && isShown(initialSectionId) ? initialSectionId : (sections[0]?.id ?? REVIEW_ID),
  );
  // Counted once, like newOnlyIds.
  const [hiddenAnswered] = useState(() =>
    newOnlyIds === null
        ? 0
        : template.sections
            .filter((x) => x.audience !== "info")
            .flatMap((x) => x.questions)
            .filter((q) => q.type !== "info" && !q.deprecated && !newOnlyIds.has(q.id) && isAnswered(response.answers[q.id])).length,
  );
  // Answers this person changed since the form opened: the server still names the previous author.
  const [edited, setEdited] = useState<ReadonlySet<string>>(() => new Set());
  // A refetch (window focus) brings colleagues' changes: take the server's value for every
  // question this person has not edited, and leave their own edits alone.
  useEffect(() => {
    setAnswers((current) => {
      const next: Answers = { ...response.answers };
      for (const id of edited) {
        if (id in current) next[id] = current[id] as AnswerValue;
        else delete next[id];
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response.answers]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [focusQuestion, setFocusQuestion] = useState<string | null>(null);
  const autosave = useAutosave(onPatch, 800);

  const reviewFlags = useMemo(() => new Set(response.review_flags), [response.review_flags]);
  const newIds = useMemo(() => new Set(response.new_question_ids), [response.new_question_ids]);
  const missing = useMemo(() => missingRequired(template, answers), [template, answers]);

  useEffect(() => {
    if (!focusQuestion) return;
    const el = document.querySelector<HTMLElement>(`[data-question-id="${CSS.escape(focusQuestion)}"] :is(input, textarea, select)`);
    el?.focus();
    el?.scrollIntoView?.({ block: "center" });
    setFocusQuestion(null);
  }, [focusQuestion, activeId]);

  const select = (id: string) => {
    setActiveId(id);
    setSheetOpen(false);
    window.scrollTo?.({ top: 0 });
  };

  const setAnswer = (section: Section, questionId: string, value: AnswerValue) => {
    setAnswers((a) => ({ ...a, [questionId]: value }));
    setEdited((e) => new Set(e).add(questionId));
    const patch: ResponsePatch = { answers: { [questionId]: value } };
    const state = progress[section.id] ?? "not_started";
    if (state === "not_started") {
      setProgress((p) => ({ ...p, [section.id]: "in_progress" }));
      patch.section_progress = { [section.id]: "in_progress" };
    }
    autosave.queue(patch);
  };

  const markDone = (sectionId: string) => {
    setProgress((p) => ({ ...p, [sectionId]: "done" }));
    void autosave.flush();
    onMarkDone(sectionId);
  };

  const submit = async () => {
    await autosave.flush();
    onSubmit();
  };

  const toggleShowAll = () => {
    if (showAll && activeId !== REVIEW_ID && !template.sections.some((x) => x.id === activeId && hasNew(x))) {
      setActiveId(template.sections.find(hasNew)?.id ?? REVIEW_ID);
    }
    setShowAll(!showAll);
  };

  const jump = (m: MissingItem) => {
    if (newOnly && !newOnlyIds.has(m.questionId)) setShowAll(true);
    setActiveId(m.sectionId);
    setFocusQuestion(m.questionId);
  };

  const answeredByOther = (questionId: string): string | undefined => {
    if (edited.has(questionId)) return undefined;
    const by = response.answered_by?.[questionId];
    return by && by.user_id !== currentUserId ? by.name : undefined;
  };

  const index = sections.findIndex((s) => s.id === activeId);
  const active = index >= 0 ? sections[index] : undefined;
  const next = index >= 0 ? sections[index + 1] : undefined;
  const isLast = index === sections.length - 1;
  const clientSections = sections.filter((x) => x.audience !== "info");
  const doneCount = clientSections.filter((x) => progress[x.id] === "done").length;
  const pct = complete ? 100 : clientSections.length > 0 ? Math.round((doneCount / clientSections.length) * 100) : 0;
  const position = active ? t("questionnaire.position", { current: index + 1, total: sections.length }) : t("questionnaire.reviewNav");

  return (
    <div className="flex flex-col gap-6">
      {newOnlyIds !== null ? (
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
          {newOnly ? <p className="text-fg">{t("questionnaire.newOnlyNotice")}</p> : null}
          <Button variant="link" className="ml-auto" onClick={toggleShowAll}>
            {newOnly
              ? hiddenAnswered > 0
                ? t("questionnaire.showAllAnswered", { count: hiddenAnswered })
                : t("questionnaire.showAll")
              : t("questionnaire.showNewOnly")}
          </Button>
        </div>
      ) : null}
      <div className="sticky top-28 z-sticky lg:top-16 -mx-4 border-b border-border bg-canvas px-4 py-3 sm:mx-0 sm:rounded-lg sm:border sm:bg-surface sm:px-5 sm:shadow-card">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <p className="text-sm font-semibold text-heading">
              {complete
                ? t("questionnaire.progressComplete")
                : t("questionnaire.progressSummary", { done: doneCount, total: clientSections.length })}
              <span className="font-normal text-fg-muted"> · {position}</span>
            </p>
            <div
              role="progressbar"
              aria-label={t("questionnaire.progressLabel")}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pct}
              className="h-2 w-full overflow-hidden rounded-pill bg-surface-sunken"
            >
              <div className="h-full rounded-pill bg-accent transition-all duration-slow" style={{ width: `${pct}%` }} />
            </div>
          </div>
          {!readOnly ? <SavingIndicator status={autosave.status} retrying={autosave.retrying} onRetry={autosave.retryNow} /> : null}
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <Button variant="secondary" size="sm" className="md:hidden">
                <ListChecks aria-hidden="true" className="h-4 w-4" />
                {t("questionnaire.goToSection")}
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom">
              <SheetTitle className="font-heading text-xl font-semibold text-heading">{t("questionnaire.sections")}</SheetTitle>
              <SheetDescription className="sr-only">{t("questionnaire.goToSection")}</SheetDescription>
              <SectionNav
                sections={sections}
                progress={progress}
                activeId={activeId}
                onSelect={select}
                includeReview={!readOnly}
                label={t("questionnaire.sectionsSheet")}
              />
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <div className="grid gap-6 md:grid-cols-shell md:gap-8">
        <aside className="hidden md:sticky md:top-40 md:block md:self-start">
          <SectionNav sections={sections} progress={progress} activeId={activeId} onSelect={select} includeReview={!readOnly} />
        </aside>
        <div className="flex min-w-0 flex-col gap-4">
          <div className="rounded-lg border border-border bg-surface p-6 shadow-card sm:p-8">
            {active ? (
              <section aria-labelledby={`section-${active.id}`} className="flex flex-col">
                {active.audience === "info" ? (
                  <p className="-mx-6 -mt-6 mb-6 rounded-t-lg bg-surface-sunken px-6 py-3 text-sm font-semibold text-fg sm:-mx-8 sm:-mt-8 sm:px-8">
                    {t("questionnaire.infoBanner")}
                  </p>
                ) : null}
                <h2 id={`section-${active.id}`}>{active.title}</h2>
                {active.summary ? (
                  <div className="mt-4 rounded-md border-l-4 border-accent bg-secondary-subtle px-4 py-3">
                    <p className="text-sm font-semibold uppercase tracking-wide text-secondary-hover">{t("questionnaire.whatWePlan")}</p>
                    <Markdown source={active.summary} className="text-fg" />
                  </div>
                ) : null}
                <div className="mt-2 flex flex-col">
                  {active.questions
                    .filter((q) => !q.deprecated && (!newOnly || newOnlyIds.has(q.id)))
                    .map((q) => {
                      if (q.type === "info" || active.audience === "info") return <InfoBlock key={q.id} question={q} />;
                      const Comp = COMPONENTS[q.type];
                      if (!Comp) return null;
                      return (
                        <Comp
                          key={q.id}
                          question={q}
                          value={answers[q.id]}
                          onChange={(v) => setAnswer(active, q.id, v)}
                          disabled={readOnly}
                          answeredBy={answeredByOther(q.id)}
                          flags={{ isNew: newIds.has(q.id), needsReview: reviewFlags.has(q.id) }}
                        />
                      );
                    })}
                </div>
                <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-border pt-6">
                  {!readOnly && active.audience === "client" ? (
                    progress[active.id] === "done" ? (
                      <span className="text-sm font-semibold text-heading">{t("questionnaire.markedDone")}</span>
                    ) : (
                      <Button variant="secondary" onClick={() => markDone(active.id)}>
                        {t("questionnaire.markDone")}
                      </Button>
                    )
                  ) : null}
                  {next ? (
                    <Button variant="ghost" className="ml-auto" onClick={() => select(next.id)}>
                      {t("questionnaire.nextSection")}
                    </Button>
                  ) : isLast && !readOnly ? (
                    <Button variant="ghost" className="ml-auto" onClick={() => select(REVIEW_ID)}>
                      {t("questionnaire.goToReview")}
                    </Button>
                  ) : null}
                </div>
              </section>
            ) : (
              <ReviewPanel missing={missing} onJump={jump} onSubmit={() => void submit()} submitting={submitting} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
